import React, { useEffect, useMemo, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash, Chalkboard, EnvelopeSimple, Phone, BookOpen } from "@phosphor-icons/react";
import { toast } from "sonner";

const COLOR_PALETTE = [
  "#3A5A40", "#D97757", "#7C5E99", "#E8A33D", "#2E7D32", "#1976D2", "#C62828", "#5C6A5D", "#8B6F47", "#0277BD",
];

const EMPTY_FORM = { name: "", hourly_rate: "", color: COLOR_PALETTE[0], email: "", phone: "", subject: "", notes: "" };

export default function TeachersPage() {
  const { user } = useAuth();
  const currency = user?.currency || "TRY";
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({}); // teacher_id → {lesson_count, completed_count, earned_total}
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [tRes, lRes] = await Promise.all([
        api.get("/teachers"),
        api.get("/lessons", { params: { start: "1900-01-01", end: "2099-12-31" } }),
      ]);
      setTeachers(tRes.data);
      // Aggregate per-teacher stats
      const agg = {};
      for (const l of lRes.data) {
        const tid = l.teacher_id;
        if (!tid) continue;
        const row = agg[tid] || { lesson_count: 0, completed_count: 0, earned_total: 0 };
        row.lesson_count += 1;
        if (l.status === "completed") row.completed_count += 1;
        if (l.status === "completed" || l.status === "cancelled_paid") row.earned_total += Number(l.amount || 0);
        agg[tid] = row;
      }
      setStats(agg);
    } catch {
      toast.error("Öğretmenler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }
  function openEdit(t) {
    setEditingId(t.id);
    setForm({
      name: t.name || "",
      hourly_rate: t.hourly_rate ?? "",
      color: t.color || COLOR_PALETTE[0],
      email: t.email || "",
      phone: t.phone || "",
      subject: t.subject || "",
      notes: t.notes || "",
    });
    setDialogOpen(true);
  }

  async function save() {
    if (!form.name.trim()) return toast.error("Öğretmen adı gerekli.");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        hourly_rate: form.hourly_rate === "" ? 0 : Number(form.hourly_rate),
        color: form.color,
        email: form.email || null,
        phone: form.phone,
        subject: form.subject,
        notes: form.notes,
      };
      if (editingId) {
        await api.patch(`/teachers/${editingId}`, payload);
        toast.success("Öğretmen güncellendi.");
      } else {
        await api.post("/teachers", payload);
        toast.success("Öğretmen eklendi.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(t) {
    if (!window.confirm(`"${t.name}" öğretmenini silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/teachers/${t.id}`);
      toast.success("Öğretmen silindi.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi.");
    }
  }

  const totalLessons = useMemo(() => Object.values(stats).reduce((s, r) => s + (r.lesson_count || 0), 0), [stats]);

  return (
    <div className="space-y-6" data-testid="teachers-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-2">
            <Chalkboard size={32} weight="duotone" className="text-[#3A5A40]" /> Öğretmenler
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Kurumdaki öğretmenleri yönetin. Her ders bir öğretmene atanır; aynı saatte aynı öğretmen veya aynı öğrenci için çakışma olmaz.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="add-teacher-button">
          <Plus size={16} className="mr-1" weight="bold" /> Yeni Öğretmen
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatBox label="Toplam öğretmen" value={teachers.length} testId="stat-teachers" />
        <StatBox label="Toplam ders" value={totalLessons} testId="stat-lessons" />
        <StatBox label="Aktif kurum" value={teachers.length > 0 ? "Evet" : "Hayır"} testId="stat-active" />
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Yükleniyor…</div>
      ) : teachers.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-lg p-12 text-center">
          <Chalkboard size={40} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
          <h3 className="font-display font-bold text-lg mb-1">Henüz öğretmen yok</h3>
          <p className="text-sm text-muted-foreground mb-4">İlk öğretmeninizi ekleyerek başlayın. Sonra her dersi bir öğretmene atayabilirsiniz.</p>
          <Button onClick={openCreate} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="add-first-teacher-button">
            <Plus size={16} className="mr-1" weight="bold" /> İlk Öğretmeni Ekle
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="teachers-grid">
          {teachers.map((t) => {
            const s = stats[t.id] || { lesson_count: 0, completed_count: 0, earned_total: 0 };
            return (
              <div key={t.id} className="bg-card border border-border rounded-lg p-5 space-y-3" data-testid={`teacher-card-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold" style={{ background: t.color }}>
                      {t.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-bold leading-tight truncate">{t.name}</div>
                      {t.subject && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><BookOpen size={12} /> {t.subject}</div>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(t)} className="p-1.5 rounded-md hover:bg-muted" title="Düzenle" data-testid={`edit-teacher-${t.id}`}>
                      <Pencil size={14} weight="duotone" />
                    </button>
                    <button onClick={() => remove(t)} className="p-1.5 rounded-md hover:bg-[#FFE0E0] text-[#C62828]" title="Sil" data-testid={`delete-teacher-${t.id}`}>
                      <Trash size={14} weight="duotone" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1 text-xs text-muted-foreground">
                  {t.email && <div className="flex items-center gap-1.5 truncate"><EnvelopeSimple size={12} /> {t.email}</div>}
                  {t.phone && <div className="flex items-center gap-1.5"><Phone size={12} /> {t.phone}</div>}
                </div>
                <div className="flex flex-wrap gap-2 text-xs pt-2 border-t border-border">
                  <span className="px-2 py-0.5 rounded-full bg-muted">{s.lesson_count} ders</span>
                  <span className="px-2 py-0.5 rounded-full bg-[#E8F5E9] text-[#2E7D32]">{s.completed_count} tamamlanan</span>
                  {Number(t.hourly_rate) > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-[#FFF3E0] text-[#E65100] font-medium">
                      {formatCurrency(t.hourly_rate, currency)}/saat
                    </span>
                  )}
                </div>
                {s.earned_total > 0 && (
                  <div className="text-xs flex items-center justify-between pt-1">
                    <span className="text-muted-foreground">Toplam kazanç:</span>
                    <span className="font-display font-bold text-[#3A5A40]">{formatCurrency(s.earned_total, currency)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="teacher-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Öğretmen Düzenle" : "Yeni Öğretmen"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Ad Soyad *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn: Ayşe Yılmaz" data-testid="teacher-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Saatlik ücret ({currency})</Label>
                <Input type="number" min="0" step="50" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} placeholder="0" data-testid="teacher-rate-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Branş / Konu</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Matematik, İngilizce…" data-testid="teacher-subject-input" />
              </div>
              <div className="space-y-1.5">
                <Label>E-posta</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="opsiyonel" data-testid="teacher-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="opsiyonel" data-testid="teacher-phone-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Renk (takvimde gösterimi)</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-8 h-8 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-foreground scale-110" : "hover:scale-105"}`}
                    style={{ background: c }}
                    aria-label={`Renk seç ${c}`}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notlar</Label>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Bu öğretmenle ilgili özel notlar…"
                className="flex w-full rounded-md border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="teacher-notes-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="teacher-cancel-button">İptal</Button>
            <Button onClick={save} disabled={saving} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="teacher-save-button">
              {saving ? "Kaydediliyor…" : editingId ? "Güncelle" : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, testId }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-xl md:text-2xl font-bold mt-1 text-[#3A5A40]">{value}</div>
    </div>
  );
}
